import CognitoUtil from 'APP_ROOT/components/aws/cognito/cognitoUtil';
import BuildSettings from 'APP_ROOT/components/buildSettings';
import lsUtil from 'APP_ROOT/components/common/lsUtil';

const JWT_EXPIRATION_DURATION = 10 * 60; // 10 minutes (5 minutes buffer from actual 15 minutes expiration)
const PROACTIVE_REFRESH_WINDOW_SECONDS = 5 * 60; // Refresh if token has less than 5 minutes of life left
const MAX_RETRIES = 3; // Number of times to retry API calls after refreshing token

let tokenRefreshTimeout = null;

export async function apiRequest(url = '', jwt, method, data = {}, signal) {

    // Default options are marked with *
    const requestObject = {
        method: method, // *GET, POST, PUT, DELETE, etc.
        mode: 'cors', // no-cors, *cors, same-origin
        cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
        // credentials: 'same-origin', // include, *same-origin, omit
        headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + jwt
        },
        redirect: 'follow', // manual, *follow, error
        referrerPolicy: 'no-referrer' // no-referrer, *client
    };
    if (method !== 'GET') {
        requestObject.body = JSON.stringify(data); // body data type must match "Content-Type" header
    }

    if(signal) {
        requestObject.signal = signal;
    }

    const response = await fetch(url, requestObject);
    const contentType = response.headers.get("content-type");
    const location = response.headers.get("location");

    let body;
    if (contentType && contentType.indexOf("application/json") !== -1) {
        body = await response.json();
        body.location = location;
    } else {
        body = undefined;
    }

    if (response.status > 300) {
        let errorMessage = "";
        if (body && body.error) {
            errorMessage = body.error;
        } else if (body && body.message) {
            errorMessage = body.message;
        } else {
            errorMessage = `Request failed with status ${response.status}`;
        }
        const error = new Error(errorMessage);
        error.code = response.status;
        return Promise.reject(error);
    }

    return Promise.resolve(body);
}

export function getApiBaseUrl(endpoint) {
    return BuildSettings.kloudApiUrl(endpoint);
}

export function getNewAPIJWT(userPoolIdTokenJWT, userAccountDetails) {
    
    if (!userPoolIdTokenJWT) return Promise.reject(new Error('userPoolIdTokenJWT is required'));

    const apiBaseUrl = getApiBaseUrl('/user-v1');

    const data = {};

    if (userAccountDetails) {
        data.accountExternalId = userAccountDetails.account.accountId;
        // {accountExternalId: "123000...45assd"}
    }
    //data.accountExternalId = "16e1188f-a747-45de-84a3-ef849f82eae2";

    const method = 'POST';
    return apiRequest(`${apiBaseUrl}/auth/token`, userPoolIdTokenJWT, method, data)
    .then(
        (response) => {
            //{"token": "eyJ0eXAiOiJKV1QiLCJhbGciOiJIU...TRUNCATED"}
            if (!response.token) {
                if (response.message && response.message.includes("NotAuthorizedException")) {
                    // {"message": "An error occurred (NotAuthorizedException) when c..."}
                    const error = new Error('NotAuthorizedException');
                    error.code = 401;
                    return Promise.reject(error);
                }
                console.error(response.message);
                return Promise.reject(new Error('Unknown error occurred'));
            }
            return Promise.resolve(response.token);
        },
        (error) => {
            return Promise.reject(error); // passed to .catch below
        }
    )
    .catch((error) => { // catches other exceptions and promise rejects above
        return Promise.reject(error);
    });
}

export function setLocalAPIJWT (apiJWT) {
    lsUtil.setItem(lsUtil.RC_API_JWT, apiJWT);
    setLocalAPIJWTExpiration();
}

export function clearLocalAPIJWT () {
    lsUtil.removeItem(lsUtil.RC_API_JWT);
    lsUtil.removeItem(lsUtil.RC_API_JWT_EXPIRATION);
}

function setLocalAPIJWTExpiration () {
    const currentLocalUnixTime = Math.floor(Date.now() / 1000);
    const expiration = currentLocalUnixTime + JWT_EXPIRATION_DURATION;
    lsUtil.setItem(lsUtil.RC_API_JWT_EXPIRATION, expiration);
}

function getLocalAPIJWTExpiration () {
    return lsUtil.getItem(lsUtil.RC_API_JWT_EXPIRATION);
}

export function getLocalAPIJWT () {

    const expiration = getLocalAPIJWTExpiration();

    if (!expiration) return null;

    const currentLocalUnixTime = Math.floor(Date.now() / 1000);
    if (expiration < currentLocalUnixTime) {
        lsUtil.removeItem(lsUtil.RC_API_JWT);
        lsUtil.removeItem(lsUtil.RC_API_JWT_EXPIRATION);
        return null;
    };

    return lsUtil.getItem(lsUtil.RC_API_JWT);
}

export function getAPIJWT(stage) {
    const apiJWT = getLocalAPIJWT();
    if (apiJWT) {
        return Promise.resolve(apiJWT);
    } else {
        console.warn("getAPIJWT: refreshing apiJWT");
        return RcAPI.refreshTempCredentials(stage);
    }
};

export class RcAPI {

    // Static property to track in-progress refresh
    static _refreshPromise = null;

    static refreshTempCredentials (stage, retryCount = 0) {

        // If there's already a refresh in progress, return that promise
        if (RcAPI._refreshPromise) {
            console.log("RcAPI refreshTempCredentials already in progress, returning existing promise");
            return RcAPI._refreshPromise;
        }

        const userPoolIdTokenJWT = CognitoUtil.getIdTokenJWT();
        const userAccount = CognitoUtil.getUserAccountsDetails();

        RcAPI._refreshPromise = getNewAPIJWT(userPoolIdTokenJWT, userAccount)
            .then(
                (apiJWT) => {
                    setLocalAPIJWT(apiJWT);
                    return apiJWT;
                }
            )
            .catch( (error) => {
                if (error.code && error.code === 401 && retryCount < MAX_RETRIES) {
                    // Unauthorized due to expired temporary user session, try to refresh
                    return new Promise((resolve, reject) => {
                        CognitoUtil.refreshLogin(
                            () => {
                                // Clear the promise before recursive call to avoid treating it as concurrent
                                RcAPI._refreshPromise = null;

                                // IMPORTANT: Not returning RcAPI.refreshTempCredentials promise. Instead, calling "resolve" or "reject" functions from parent promise wrapper b/c CognitoUtil.refreshLogin does not return a promise
                                RcAPI.refreshTempCredentials(stage, retryCount + 1).then(resolve, reject);
                            },
                            reject);
                    });
                }
                throw error;
            })
            .finally(() => {
                // Clear the promise when done (success or failure)
                RcAPI._refreshPromise = null;
            });

        return RcAPI._refreshPromise;
    }

    static async apiRequest(url, method, data = {}, stage, abortSignal) {

        return getAPIJWT(stage)
            .then((apiJWT) => apiRequest(url, apiJWT, method, data, abortSignal))
            .catch(error => {
                console.error("RcAPI apiRequest error", error);
                throw error;
            });
    }

    static requestBasicCreateRavenMedia = (stage, ravenId, cameraType, timestamp, duration, eventType, abortController) => {

        if (!stage) return Promise.reject(new Error('stage is required'));
        if (!ravenId) return Promise.reject(new Error('ravenId is required'));
        if (!cameraType) return Promise.reject(new Error('cameraType is required'));
        if (!timestamp) return Promise.reject(new Error('timestamp is required'));
        if (!duration) return Promise.reject(new Error('duration is required'));

        const apiBaseUrl = getApiBaseUrl('/user-v1');
        const apiFullUrl = `${apiBaseUrl}/ravens/${ravenId}/media`;
        const method = 'POST';

        const payload = {
            timestamp: timestamp, // int, unix timestamp
            cameraType: cameraType,
            duration: duration
        }

        if (eventType) {
            payload.eventType = eventType; // string, optional
        }

        return RcAPI.apiRequest(apiFullUrl, method, payload, stage, abortController?.signal)
        .catch((error) => {            
            console.error("RcAPI requestBasicCreateRavenMedia error", error);
            throw error;
        });
    }

    static getRavenMedia = (stage, ravenId, cameraType, timestamp, abortController) => {

        if (!stage) return Promise.reject(new Error('stage is required'));
        if (!ravenId) return Promise.reject(new Error('ravenId is required'));
        if (!cameraType) return Promise.reject(new Error('cameraType is required'));
        if (!timestamp) return Promise.reject(new Error('timestamp is required'));

        const queryString = `type=preview_video&signed_urls=true&ts=${timestamp}`;
        
        const apiBaseUrl = getApiBaseUrl('/user-v1');
        const apiFullUrl = `${apiBaseUrl}/ravens/${ravenId}/media?${queryString}`;
        const method = 'GET';


        return RcAPI.apiRequest(apiFullUrl, method, undefined, stage, abortController?.signal)
        .catch((error) => {            
            console.error("RcAPI getRavenMedia error", error);
            throw error;
        });
    }

    static checkRavenMediaStatus = (stage, ravenId, mediaId, abortController) => {

        if (!stage) return Promise.reject(new Error('stage is required'));
        if (!ravenId) return Promise.reject(new Error('ravenId is required'));
        if (!mediaId) return Promise.reject(new Error('mediaId is required'));

        const apiBaseUrl = getApiBaseUrl('/user-v1');
        const apiFullUrl = `${apiBaseUrl}/ravens/${ravenId}/media/${mediaId}`;
        const method = 'GET';


        return RcAPI.apiRequest(apiFullUrl, method, undefined, stage, abortController?.signal)
        .catch((error) => {            
            console.error("RcAPI checkRavenMediaStatus error", error);
            throw error;
        });
    }

    static acceptUserAgreement = (stage, abortController) => {
        
        if (!stage) return Promise.reject(new Error('stage is required'));
        const apiBaseUrl = getApiBaseUrl('/user-v1');
        const apiFullUrl = `${apiBaseUrl}/account/user-agreement`;
        const method = 'POST';
        // no payload needed, just an "empty" POST to indicate acceptance

        return RcAPI.apiRequest(apiFullUrl, method, undefined, stage, abortController?.signal)
        .catch((error) => {            
            console.error("RcAPI acceptUserAgreement error", error);
            throw error;
        });
    }

    static createOrder = (stage, orderPayload, abortController) => {
        if (!stage) return Promise.reject(new Error('stage is required'));
        if (!orderPayload) return Promise.reject(new Error('orderPayload is required'));

        const apiJWT = getLocalAPIJWT();
        if (!apiJWT) {
            return RcAPI.refreshTempCredentials(stage)
                .then(refreshedApiJWT => {
                    return RcAPI.createOrder(stage, orderPayload, abortController);
                });
        }

        const apiBaseUrl = getApiBaseUrl('/user-v1');
        const method = 'POST';

        return apiRequest(`${apiBaseUrl}/orders`, apiJWT, method, orderPayload, abortController?.signal)
            .then(
                (body) => { return Promise.resolve(body); },
                (error) => { return Promise.reject(error); }
            )
            .catch((error) => {
                if (error.code) {
                    switch (error.code) {
                        case 401:
                            // apiJWT expired, get a new one and try again
                            return RcAPI.refreshTempCredentials(stage)
                                .then(refreshedApiJWT => {
                                    // Don't re-call createOrder to avoid loops.
                                    // Instead, directly call apiRequest with the new token.
                                    return apiRequest(`${apiBaseUrl}/orders`, refreshedApiJWT, method, orderPayload, abortController?.signal);
                                });
                        default:
                            break;
                    }
                }
                return Promise.reject(error);
            });
    }

    static getOrders = (stage, abortController = null) => {

        /* deprecated response format example:
            {
                "orders": [
                    {
                        "order_id": 157,
                        "order_number": "5853638557747",
                        "account_id": 1486,
                        "order_status": "PENDING_RAVENS",
                        "shopify_data": {
                            "lineItems": [
                                {
                                    "uuid": "",
                                    "variantId": "gid://shopify/ProductVariant/43876912922675",
                                    "quantity": 1
                                }
                            ],
                            "status": "Paid",
                            "purchasingEntity": null,
                            "billingAddress": {
                                "firstName": "Robert",
                                "lastName": "Watson",
                                "address1": "441 Maclaren Street",
                                "address2": "408",
                                "city": "New York",
                                "province": "New York",
                                "country": "United States",
                                "zip": "10001",
                                "phone": null
                            },
                            "shippingAddress": {
                                "firstName": "Robert",
                                "lastName": "Watson",
                                "address1": "441 Maclaren Street",
                                "address2": "408",
                                "city": "New York",
                                "province": "New York",
                                "country": "United States",
                                "zip": "10001",
                                "phone": null
                            }
                        }
                    }
                ]
            }
        */
        /* new response format example:
        {
            "results": [
                {
                    "orderId": 158,
                    "orderNumber": "5854520836147",
                    "accountId": 1487,
                    "orderStatus": "PENDING_RAVENS",
                    "orderDetails": {
                        "lineItems": [
                            {
                                "uuid": "",
                                "variantId": "gid://shopify/ProductVariant/43876911972403",
                                "quantity": 1
                            },
                            {
                                "uuid": "",
                                "variantId": "gid://shopify/ProductVariant/43876911415347",
                                "quantity": 1
                            },
                            {
                                "uuid": "",
                                "variantId": "gid://shopify/ProductVariant/43876912922675",
                                "quantity": 1
                            }
                        ],
                        "status": "Paid",
                        "customer": {
                            "id": "gid://shopify/Customer/8028440625203",
                            "firstName": "Robert",
                            "lastName": "Watson",
                            "defaultEmailAddress": {
                                "emailAddress": "rob+dev+20260209.02@klashwerks.com"
                            }
                        },
                        "billingAddress": {
                            "firstName": "Robert",
                            "lastName": "Watson",
                            "address1": "441 Maclaren Street",
                            "address2": "408",
                            "city": "New York",
                            "province": "New York",
                            "country": "United States",
                            "zip": "10001",
                            "phone": null
                        },
                        "shippingAddress": {
                            "firstName": "Robert",
                            "lastName": "Watson",
                            "address1": "441 Maclaren Street",
                            "address2": "408",
                            "city": "New York",
                            "province": "New York",
                            "country": "United States",
                            "zip": "10001",
                            "phone": null
                        }
                    }
                }
            ],
            "next": null,
            "prev": null,
            "total": 1
        }

        */
        if (!stage) return Promise.reject(new Error('stage is required'));

        const apiJWT = getLocalAPIJWT();
        if (!apiJWT) {
            return RcAPI.refreshTempCredentials(stage)
                .then(refreshedApiJWT => {
                    return RcAPI.getOrders(stage, abortController);
                });
        }

        const apiBaseUrl = getApiBaseUrl('/user-v1');
        const method = 'GET';

        return apiRequest(`${apiBaseUrl}/orders`, apiJWT, method, undefined, abortController?.signal)
            .then(
                (body) => { return Promise.resolve(body); },
                (error) => { return Promise.reject(error); }
            )
            .catch((error) => {
                if (error.code) {
                    switch (error.code) {
                        case 401:
                            // apiJWT expired, get a new one and try again
                            return RcAPI.refreshTempCredentials(stage)
                                .then(refreshedApiJWT => {
                                    // Don't re-call getOrders to avoid loops.
                                    // Instead, directly call apiRequest with the new token.
                                    return apiRequest(`${apiBaseUrl}/orders`, refreshedApiJWT, method, undefined, abortController?.signal);
                                });
                        default:
                            break;
                    }
                }
                return Promise.reject(error);
            });
    }
}